/**
 * ObstacleGeometry - Clash Obstacle Math & Configurations
 * Encapsulates coordinate generators, holding areas, step positioning,
 * loop geometry, and Catmull-Rom splines for No Passing Lanes, Jumps,
 * Jails, Bars, Timeouts, Stop Signs, Detours, and Roundabouts.
 */

import { getTrackXY as defaultGetTrackXY } from './BoardGeometry.js';

export const CLASH_DEFAULTS_481 = Object.freeze({
  noPassingLaneStarts: [40, 175, 275, 415],
  jumpStarts: [25, 195, 295, 435, 75, 145, 245, 365],
  jumpDests: [60, 160, 335, 395, 110, 125, 280, 400],
  jailHoles: [110, 315],
  barHoles: [125, 375],
  timeoutHoles: [75, 255],
  detourHoles: [15, 235],
  roundAboutHoles: [145, 355],
  signsStarts: [90, 215, 450, 465],
  stopSignHoles: [35, 70, 105, 155, 205, 265, 345, 425],
  diceHoles: [50, 185, 285, 410],
  wheelHoles: [65, 160, 260, 385],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 75,
  optSigns1Hole: 90,
  optJail1Hole: 110,
  optBar1Hole: 125,
  optRoundAbout1Hole: 145,
  optJump2Dest: 160,
  optNoPassingLane2Start: 175,
  optJump2Start: 195,
  optSigns2Hole: 215,
  optDetour2Hole: 235,
  optTimeout2Hole: 255,
  optNoPassingLane3Start: 275,
  optJump3Start: 295,
  optJail2Hole: 315,
  optJump3Dest: 335,
  optRoundAbout2Hole: 355,
  optBar2Hole: 375,
  optJump4Dest: 395,
  optNoPassingLane4Start: 415,
  optJump4Start: 435,
  optJump5Start: 75,
  optJump5Dest: 110,
  optJump6Start: 145,
  optJump6Dest: 125,
  optJump7Start: 245,
  optJump7Dest: 280,
  optJump8Start: 365,
  optJump8Dest: 400,
  optSigns3Hole: 450,
  optSigns4Hole: 465,
  optStopSign1Hole: 35,
  optStopSign2Hole: 70,
  optStopSign3Hole: 105,
  optStopSign4Hole: 155,
  optStopSign5Hole: 205,
  optStopSign6Hole: 265,
  optStopSign7Hole: 345,
  optStopSign8Hole: 425,
  optDice1Hole: 50,
  optDice2Hole: 185,
  optDice3Hole: 285,
  optDice4Hole: 410,
  optWheel1Hole: 65,
  optWheel2Hole: 160,
  optWheel3Hole: 260,
  optWheel4Hole: 385,
  optSkunkMarkerHole: 360,
  optDoubleSkunkMarkerHole: 240
});

export const CLASH_DEFAULTS_241 = Object.freeze({
  noPassingLaneStarts: [40, 95, 150, 195],
  jumpStarts: [25, 115, 145, 205, 55, 85, 135, 185],
  jumpDests: [60, 80, 185, 170, 75, 65, 160, 210],
  jailHoles: [70, 175],
  barHoles: [85, 190],
  timeoutHoles: [45, 140],
  detourHoles: [15, 125],
  roundAboutHoles: [65, 165],
  signsStarts: [50, 105, 155, 215],
  stopSignHoles: [25, 50, 75, 100, 125, 150, 175, 200],
  diceHoles: [30, 60, 130, 180],
  wheelHoles: [40, 80, 150, 200],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 45,
  optSigns1Hole: 50,
  optJail1Hole: 70,
  optBar1Hole: 85,
  optRoundAbout1Hole: 65,
  optJump2Dest: 80,
  optNoPassingLane2Start: 95,
  optJump2Start: 115,
  optSigns2Hole: 105,
  optDetour2Hole: 125,
  optTimeout2Hole: 140,
  optNoPassingLane3Start: 150,
  optJump3Start: 145,
  optJail2Hole: 175,
  optJump3Dest: 185,
  optRoundAbout2Hole: 165,
  optBar2Hole: 190,
  optJump4Dest: 170,
  optNoPassingLane4Start: 195,
  optJump4Start: 205,
  optJump5Start: 55,
  optJump5Dest: 75,
  optJump6Start: 85,
  optJump6Dest: 65,
  optJump7Start: 135,
  optJump7Dest: 160,
  optJump8Start: 185,
  optJump8Dest: 210,
  optSigns3Hole: 155,
  optSigns4Hole: 215,
  optStopSign1Hole: 25,
  optStopSign2Hole: 50,
  optStopSign3Hole: 75,
  optStopSign4Hole: 100,
  optStopSign5Hole: 125,
  optStopSign6Hole: 150,
  optStopSign7Hole: 175,
  optStopSign8Hole: 200,
  optDice1Hole: 30,
  optDice2Hole: 60,
  optDice3Hole: 130,
  optDice4Hole: 180,
  optWheel1Hole: 40,
  optWheel2Hole: 80,
  optWheel3Hole: 150,
  optWheel4Hole: 200,
  optSkunkMarkerHole: 180,
  optDoubleSkunkMarkerHole: 120
});

export const CLASH_DEFAULTS_361 = Object.freeze({
  noPassingLaneStarts: [40, 135, 215, 315],
  jumpStarts: [25, 145, 225, 325, 65, 105, 185, 275],
  jumpDests: [60, 115, 260, 295, 85, 95, 210, 310],
  jailHoles: [90, 245],
  barHoles: [105, 285],
  timeoutHoles: [65, 195],
  detourHoles: [15, 175],
  roundAboutHoles: [115, 265],
  signsStarts: [70, 165, 255, 335],
  stopSignHoles: [30, 65, 105, 150, 195, 240, 285, 330],
  diceHoles: [50, 140, 230, 320],
  wheelHoles: [70, 160, 250, 330],
  optDetour1Hole: 15,
  optJump1Start: 25,
  optNoPassingLane1Start: 40,
  optJump1Dest: 60,
  optTimeout1Hole: 65,
  optSigns1Hole: 70,
  optJail1Hole: 90,
  optBar1Hole: 105,
  optRoundAbout1Hole: 115,
  optJump2Dest: 115,
  optNoPassingLane2Start: 135,
  optJump2Start: 145,
  optSigns2Hole: 165,
  optDetour2Hole: 175,
  optTimeout2Hole: 195,
  optNoPassingLane3Start: 215,
  optJump3Start: 225,
  optJail2Hole: 245,
  optJump3Dest: 260,
  optRoundAbout2Hole: 265,
  optBar2Hole: 285,
  optJump4Dest: 295,
  optNoPassingLane4Start: 315,
  optJump4Start: 325,
  optJump5Start: 65,
  optJump5Dest: 85,
  optJump6Start: 105,
  optJump6Dest: 95,
  optJump7Start: 185,
  optJump7Dest: 210,
  optJump8Start: 275,
  optJump8Dest: 310,
  optSigns3Hole: 255,
  optSigns4Hole: 335,
  optStopSign1Hole: 30,
  optStopSign2Hole: 65,
  optStopSign3Hole: 105,
  optStopSign4Hole: 150,
  optStopSign5Hole: 195,
  optStopSign6Hole: 240,
  optStopSign7Hole: 285,
  optStopSign8Hole: 330,
  optDice1Hole: 50,
  optDice2Hole: 140,
  optDice3Hole: 230,
  optDice4Hole: 320,
  optWheel1Hole: 70,
  optWheel2Hole: 160,
  optWheel3Hole: 250,
  optWheel4Hole: 330,
  optSkunkMarkerHole: 270,
  optDoubleSkunkMarkerHole: 180
});

export function getClashDefaults(target) {
  const t = Number(target) || 481;
  if (t === 241) return CLASH_DEFAULTS_241;
  if (t === 361) return CLASH_DEFAULTS_361;
  return CLASH_DEFAULTS_481;
}

export class ObstacleGeometry {
  /**
   * Returns active No Passing Lane configurations.
   */
  static getNoPassingLanes(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optNoPassingLanes ||
      settings?.optNoPassing ||
      (isClash && settings?.optNoPassingLanes !== false && settings?.optNoPassing !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultStarts = isClash ? (getClashDefaults(clashTargetVal).noPassingLaneStarts) : [20, 50, 80, 100];
    const defaultCount = isClash ? defaultStarts.length : 2;
    const count = Math.min(4, Math.max(1, parseInt(settings?.noPassingLaneCount || settings?.optNoPassingLaneCount || settings?.noPassingLanesCount, 10) || defaultCount));
    let starts = settings?.noPassingLaneStarts || settings?.noPassingLaneHoles;
    if (!Array.isArray(starts) || !starts.length) {
      const flatStarts = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optNoPassingLane${i}Start`] || settings?.[`noPassingLane${i}Start`] || settings?.[`optNoPassingLane${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatStarts.push(Number(val));
        }
      }
      starts = flatStarts.length ? flatStarts : defaultStarts;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 100;
    const lanes = [];
    for (let i = 0; i < count; i++) {
      const s = parseInt(starts[i], 10);
      if (!isNaN(s) && s >= 10 && s <= maxHole) {
        lanes.push({ start: s, end: s + 4 });
      }
    }
    return lanes;
  }

  static isInNoPassingLane(score, lanesOrSettings = {}) {
    if (!score || score <= 0) return false;
    const lanes = Array.isArray(lanesOrSettings) ? lanesOrSettings : ObstacleGeometry.getNoPassingLanes(lanesOrSettings);
    return lanes.some(lane => score >= lane.start && score <= lane.end);
  }

  static getNoPassingLaneForHole(score, lanesOrSettings = {}) {
    const lanes = Array.isArray(lanesOrSettings) ? lanesOrSettings : ObstacleGeometry.getNoPassingLanes(lanesOrSettings);
    return lanes.find(lane => score >= lane.start && score <= lane.end) || null;
  }

  /**
   * Calculates the maximum permissible score taking into account opponent blockage in No Passing Lanes.
   */
  static getNoPassingLaneMaxScore(who, currentScore, targetScore, state = {}, settings = {}, activeTracks = []) {
    if (!settings || !settings.optNoPassingLanes) {
      return { allowedScore: targetScore, blocked: false, blockingPlayer: null };
    }
    const key = who;
    let maxAllowed = targetScore;
    let blockingPlayer = null;
    const tracks = activeTracks && activeTracks.length ? activeTracks : Object.keys((state && state.scores) || {});

    for (const other of tracks) {
      if (other === key) continue;
      const otherScore = (state && state.scores && state.scores[other]) || 0;
      if (otherScore > currentScore && ObstacleGeometry.isInNoPassingLane(otherScore, settings)) {
        const cap = otherScore - 1;
        if (cap < maxAllowed) {
          maxAllowed = cap;
          blockingPlayer = other;
        }
      }
    }

    if (maxAllowed < targetScore) {
      let resolvedCap = maxAllowed;
      let collision = true;
      while (collision && resolvedCap > currentScore) {
        collision = false;
        for (const other of tracks) {
          if (other === key) continue;
          const oScore = (state && state.scores && state.scores[other]) || 0;
          if (oScore === resolvedCap && ObstacleGeometry.isInNoPassingLane(oScore, settings)) {
            resolvedCap--;
            collision = true;
            break;
          }
        }
      }
      const finalScore = Math.max(currentScore, resolvedCap);
      return {
        allowedScore: finalScore,
        blocked: true,
        blockingPlayer
      };
    }

    return { allowedScore: targetScore, blocked: false, blockingPlayer: null };
  }

  static getJumpConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optJump ||
      settings?.optJumps ||
      (isClash && settings?.optJump !== false && settings?.optJumps !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultStarts = isClash ? (getClashDefaults(clashTargetVal).jumpStarts) : [15, 65, 5, 115, 25, 75, 45, 95];
    const defaultDests = isClash ? (getClashDefaults(clashTargetVal).jumpDests) : [35, 50, 45, 70, 55, 60, 80, 85];
    const defaultCount = isClash ? defaultStarts.length : 1;
    const count = Math.min(8, Math.max(1, parseInt(settings?.jumpCount || settings?.optJumpCount, 10) || defaultCount));
    let starts = settings?.jumpStarts;
    let dests = settings?.jumpDests;
    if (!Array.isArray(starts) || !starts.length) {
      const flatStarts = [];
      const flatDests = [];
      for (let i = 1; i <= 8; i++) {
        const sVal = settings?.[`optJump${i}Start`] || settings?.[`jump${i}Start`];
        const dVal = settings?.[`optJump${i}Dest`] || settings?.[`jump${i}Dest`];
        if (typeof sVal === 'number' || (typeof sVal === 'string' && sVal.trim() !== '')) {
          flatStarts.push(Number(sVal));
          flatDests.push(Number(dVal) || (Number(sVal) + 20));
        }
      }
      if (flatStarts.length) {
        starts = flatStarts;
        dests = flatDests;
      } else {
        starts = defaultStarts;
        dests = defaultDests;
      }
    }
    const jumps = [];
    for (let i = 0; i < count; i++) {
      const s = parseInt(starts[i], 10);
      const d = parseInt(dests?.[i], 10);
      if (!isNaN(s) && !isNaN(d)) {
        const isForward = d > s;
        jumps.push({
          id: i + 1,
          start: s,
          end: isForward ? s : s + 2,
          dest: d,
          isForward
        });
      }
    }
    return jumps;
  }

  static getJumpForHole(holeNum, jumpsOrSettings = {}) {
    const jumps = Array.isArray(jumpsOrSettings) ? jumpsOrSettings : ObstacleGeometry.getJumpConfigs(jumpsOrSettings);
    return jumps.find(j => holeNum >= j.start && holeNum <= j.end) || null;
  }

  static getJailConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optJail ||
      settings?.optJails ||
      (isClash && settings?.optJail !== false && settings?.optJails !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).jailHoles) : [25, 75];
    const defaultCount = isClash ? defaultHoles.length : 1;
    const count = Math.min(4, Math.max(1, parseInt(settings?.jailCount || settings?.optJailCount, 10) || defaultCount));
    let holes = settings?.jailHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optJail${i}Hole`] || settings?.[`jail${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const jails = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 10 && h <= maxHole) {
        jails.push({ id: i + 1, hole: h });
      }
    }
    return jails;
  }

  static getJailForHole(holeNum, jailsOrSettings = {}) {
    const jails = Array.isArray(jailsOrSettings) ? jailsOrSettings : ObstacleGeometry.getJailConfigs(jailsOrSettings);
    return jails.find(j => j.hole === holeNum) || null;
  }

  static getHoldingStepXY(hole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    const trackResolver = getTrackXYFn || defaultGetTrackXY;
    const pTrack = trackResolver(hole, who, settings);
    let dX = 0;
    let dY = 0;
    if (settings && [241, 361, 481].includes(settings.target)) {
      const row = Math.floor((hole - 1) / 60);
      const pos = (hole - 1) % 60;
      if (pos >= 55) {
        dX = (row % 2 === 0) ? 28 : -28;
      } else {
        dY = (row === 0 || row % 2 === 0) ? -28 : 28;
      }
    } else {
      const h = hole;
      if (h <= 30) dY = -34;
      else if (h <= 35) dX = 30;
      else if (h <= 65) dY = -34;
      else if (h <= 70) dX = -30;
      else dY = 34;
    }
    const cellX = pTrack.x + dX;
    const cellY = pTrack.y + dY;
    const clampedStep = Math.max(0, Math.min(4, step));
    const frac = clampedStep / 4;
    return {
      x: cellX + frac * (pTrack.x - cellX),
      y: cellY + frac * (pTrack.y - cellY)
    };
  }

  static getJailStepXY(jailHole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    return ObstacleGeometry.getHoldingStepXY(jailHole, step, who, settings, getTrackXYFn);
  }

  static getBarConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optBar ||
      settings?.optBars ||
      (isClash && settings?.optBar !== false && settings?.optBars !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).barHoles) : [40, 85];
    const defaultCount = isClash ? defaultHoles.length : 1;
    const count = Math.min(4, Math.max(1, parseInt(settings?.barCount || settings?.optBarCount, 10) || defaultCount));
    let holes = settings?.barHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optBar${i}Hole`] || settings?.[`bar${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const bars = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 10 && h <= maxHole) {
        bars.push({ id: i + 1, hole: h });
      }
    }
    return bars;
  }

  static getBarForHole(holeNum, barsOrSettings = {}) {
    const bars = Array.isArray(barsOrSettings) ? barsOrSettings : ObstacleGeometry.getBarConfigs(barsOrSettings);
    return bars.find(b => b.hole === holeNum) || null;
  }

  static getBarStepXY(barHole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    return ObstacleGeometry.getHoldingStepXY(barHole, step, who, settings, getTrackXYFn);
  }

  static getTimeoutConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optTimeout ||
      settings?.optTimeouts ||
      (isClash && settings?.optTimeout !== false && settings?.optTimeouts !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).timeoutHoles) : [30, 90];
    const defaultCount = isClash ? defaultHoles.length : 1;
    const count = Math.min(4, Math.max(1, parseInt(settings?.timeoutCount || settings?.optTimeoutCount, 10) || defaultCount));
    let holes = settings?.timeoutHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optTimeout${i}Hole`] || settings?.[`timeout${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const timeouts = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 10 && h <= maxHole) {
        timeouts.push({ id: i + 1, hole: h });
      }
    }
    return timeouts;
  }

  static getTimeoutForHole(holeNum, timeoutsOrSettings = {}) {
    const timeouts = Array.isArray(timeoutsOrSettings) ? timeoutsOrSettings : ObstacleGeometry.getTimeoutConfigs(timeoutsOrSettings);
    return timeouts.find(t => t.hole === holeNum) || null;
  }

  static getTimeoutStepXY(timeoutHole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    return ObstacleGeometry.getHoldingStepXY(timeoutHole, step, who, settings, getTrackXYFn);
  }

  static getStopSignConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optStopSign ||
      settings?.optStopSigns ||
      (isClash && settings?.optStopSign !== false && settings?.optStopSigns !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).stopSignHoles) : [20, 35, 50, 65, 80, 95, 105, 115];
    const defaultCount = isClash ? defaultHoles.length : 2;
    const count = Math.min(8, Math.max(1, parseInt(settings?.stopSignCount || settings?.optStopSignCount, 10) || defaultCount));
    let holes = settings?.stopSignHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 8; i++) {
        const val = settings?.[`optStopSign${i}Hole`] || settings?.[`stopSign${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 6) : 115;
    const stopSigns = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 5 && h <= maxHole) {
        stopSigns.push({ id: i + 1, hole: h });
      }
    }
    return stopSigns;
  }

  static getStopSignForHole(holeNum, stopSignsOrSettings = {}) {
    const signs = Array.isArray(stopSignsOrSettings) ? stopSignsOrSettings : ObstacleGeometry.getStopSignConfigs(stopSignsOrSettings);
    return signs.find(s => s.hole === holeNum) || null;
  }

  static getDetourConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optDetour ||
      settings?.optDetours ||
      (isClash && settings?.optDetour !== false && settings?.optDetours !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).detourHoles) : [10, 60];
    const defaultCount = isClash ? defaultHoles.length : 1;
    const count = Math.min(4, Math.max(1, parseInt(settings?.detourCount || settings?.optDetourCount, 10) || defaultCount));
    let holes = settings?.detourHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optDetour${i}Hole`] || settings?.[`detour${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const detours = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 10 && h <= maxHole) {
        detours.push({ id: i + 1, hole: h });
      }
    }
    return detours;
  }

  static getDetourForHole(holeNum, detoursOrSettings = {}) {
    const detours = Array.isArray(detoursOrSettings) ? detoursOrSettings : ObstacleGeometry.getDetourConfigs(detoursOrSettings);
    return detours.find(d => d.hole === holeNum) || null;
  }

  static catmullRomPath(pts) {
    if (!pts || pts.length < 2) return '';
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = i > 0 ? pts[i - 1] : pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = i < pts.length - 2 ? pts[i + 2] : p2;

      const cp1x = (p1.x + (p2.x - p0.x) / 6).toFixed(1);
      const cp1y = (p1.y + (p2.y - p0.y) / 6).toFixed(1);
      const cp2x = (p2.x - (p3.x - p1.x) / 6).toFixed(1);
      const cp2y = (p2.y - (p3.y - p1.y) / 6).toFixed(1);

      d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    return d;
  }

  static computeLoopGeometry(holeNum, who = 'p1', isRoundAbout = false, settings = {}, getTrackXYFn = null) {
    const trackResolver = getTrackXYFn || defaultGetTrackXY;
    const pEntry = trackResolver(holeNum, who, settings);
    const pExit = trackResolver(holeNum + 1, who, settings);

    const dx = pExit.x - pEntry.x;
    const dy = pExit.y - pEntry.y;
    const len = Math.hypot(dx, dy) || 12;
    const Tx = dx / len;
    const Ty = dy / len;

    let Nx = 0;
    let Ny = 0;

    if (settings && [241, 361, 481].includes(settings.target)) {
      const row = Math.floor((holeNum - 1) / 60);
      const pos = (holeNum - 1) % 60;
      if (pos >= 55) {
        Nx = (row % 2 === 0) ? 1 : -1;
        Ny = 0;
      } else {
        Nx = 0;
        Ny = (row === 0 || row % 2 === 0) ? -1 : 1;
      }
    } else {
      const h = holeNum;
      if (h <= 30) {
        Nx = 0; Ny = -1;
      } else if (h <= 35) {
        Nx = 1; Ny = 0;
      } else if (h <= 65) {
        Nx = 0; Ny = isRoundAbout ? 1 : -1;
      } else if (h <= 70) {
        Nx = -1; Ny = 0;
      } else {
        Nx = 0; Ny = 1;
      }
    }

    const Mx = (pEntry.x + pExit.x) / 2;
    const My = (pEntry.y + pExit.y) / 2;

    const Rdist = isRoundAbout ? 20 : 9;
    const Rn = isRoundAbout ? 15 : 6.5;
    const Rt = isRoundAbout ? 16 : 7.5;
    const betaMax = isRoundAbout ? 2.50 : 2.40;

    const Cx = Mx + Nx * Rdist;
    const Cy = My + Ny * Rdist;

    return {
      pEntry,
      pExit,
      Mx,
      My,
      Tx,
      Ty,
      Nx,
      Ny,
      Cx,
      Cy,
      Rdist,
      Rn,
      Rt,
      betaMax
    };
  }

  static getDetourStepXY(detourHole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    const geom = ObstacleGeometry.computeLoopGeometry(detourHole, who, false, settings, getTrackXYFn);
    if (step <= 0) return geom.pEntry;
    if (step >= 8) return geom.pExit;

    const f = Math.max(0, Math.min(8, step)) / 8;
    const beta = (f - 0.5) * 2 * geom.betaMax;
    const alongT = Math.sin(beta);
    const alongN = Math.cos(beta);

    return {
      x: geom.Cx + (alongT * geom.Rt) * geom.Tx + (alongN * geom.Rn) * geom.Nx,
      y: geom.Cy + (alongT * geom.Rt) * geom.Ty + (alongN * geom.Rn) * geom.Ny
    };
  }

  static getRoundAboutConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optRoundAbout ||
      settings?.optRoundAbouts ||
      (isClash && settings?.optRoundAbout !== false && settings?.optRoundAbouts !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultHoles = isClash ? (getClashDefaults(clashTargetVal).roundAboutHoles) : [35, 70];
    const defaultCount = isClash ? defaultHoles.length : 1;
    const count = Math.min(4, Math.max(1, parseInt(settings?.roundAboutCount || settings?.optRoundAboutCount, 10) || defaultCount));
    let holes = settings?.roundAboutHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings?.[`optRoundAbout${i}Hole`] || settings?.[`roundAbout${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const roundabouts = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 10 && h <= maxHole) {
        roundabouts.push({ id: i + 1, hole: h });
      }
    }
    return roundabouts;
  }

  static getRoundAboutForHole(holeNum, roundaboutsOrSettings = {}) {
    const roundabouts = Array.isArray(roundaboutsOrSettings) ? roundaboutsOrSettings : ObstacleGeometry.getRoundAboutConfigs(roundaboutsOrSettings);
    return roundabouts.find(r => r.hole === holeNum) || null;
  }

  static getRoundAboutStepXY(roundAboutHole, step, who = 'p1', settings = {}, getTrackXYFn = null) {
    const geom = ObstacleGeometry.computeLoopGeometry(roundAboutHole, who, true, settings, getTrackXYFn);
    if (step <= 0) return geom.pEntry;
    if (step >= 8) return geom.pExit;

    const f = Math.max(0, Math.min(8, step)) / 8;
    const beta = (f - 0.5) * 2 * geom.betaMax;
    const alongT = Math.sin(beta);
    const alongN = Math.cos(beta);

    return {
      x: geom.Cx + (alongT * geom.Rt) * geom.Tx + (alongN * geom.Rn) * geom.Nx,
      y: geom.Cy + (alongT * geom.Rt) * geom.Ty + (alongN * geom.Rn) * geom.Ny
    };
  }

  static getSignConfigs(settings = {}) {
    if (settings?.brand && settings.brand !== 'clash') return [];
    const isClashTarget = [241, 361, 481].includes(Number(settings?.target));
    const isClashBrand = settings?.brand === 'clash' || settings?.boardId === 'board-cribbage-clash' || settings?.boardId === 'board-cribbage-clash-square';
    const isClash = isClashTarget || isClashBrand;
    const isEnabled = Boolean(
      settings?.optSigns ||
      settings?.optSign ||
      (isClash && settings?.optSigns !== false && settings?.optSign !== false)
    );
    if (!isEnabled) return [];
    const clashTargetVal = Number(settings?.target) || 481;
    const defaultStarts = isClash ? (getClashDefaults(clashTargetVal).signsStarts || [90, 215, 450, 465]) : [45, 55, 95, 105];
    const defaultBlocks = [
      { id: 1, start: defaultStarts[0] || 45, size: 2, signs: ['Plus 5', 'Roll The Dice'] },
      { id: 2, start: defaultStarts[1] || 55, size: 4, signs: ['Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2'] },
      { id: 3, start: defaultStarts[2] || 95, size: 2, signs: ['Undo', 'Score Plus x3'] },
      { id: 4, start: defaultStarts[3] || 105, size: 4, signs: ['Plus 5', 'Minus 10', 'Go To Jail', 'Roll The Dice'] }
    ];
    const defaultCount = isClash ? defaultStarts.length : 4;
    const count = Math.min(8, Math.max(1, parseInt(settings?.signsCount || settings?.optSignsCount, 10) || defaultCount));
    let blocks = settings?.signsBlocks;
    if (!Array.isArray(blocks) || !blocks.length) {
      const flatBlocks = [];
      for (let i = 1; i <= 8; i++) {
        const val = settings?.[`optSigns${i}Hole`] || settings?.[`signs${i}Hole`] || settings?.[`optSigns${i}Start`] || settings?.[`signs${i}Start`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatBlocks.push({
            id: i,
            start: Number(val),
            size: i % 2 === 0 ? 4 : 2,
            signs: i % 2 === 0 ? ['Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2'] : ['Plus 5', 'Roll The Dice']
          });
        }
      }
      blocks = flatBlocks.length ? flatBlocks : defaultBlocks;
    }
    const maxHole = isClash ? (clashTargetVal - 11) : 110;
    const result = [];
    for (let i = 0; i < count; i++) {
      const b = blocks[i] || {};
      const s = parseInt(b.start, 10);
      const sz = parseInt(b.size, 10) === 4 ? 4 : 2;
      const pegLength = sz === 2 ? 3 : 5;
      if (!isNaN(s) && s >= 10 && s <= maxHole) {
        const fallbackSigns = sz === 2 ? ['Plus 5', 'Roll The Dice'] : ['Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2'];
        const initialSigns = Array.isArray(b.signs) && b.signs.length ? b.signs.slice(0, sz) : fallbackSigns;
        result.push({
          id: i + 1,
          start: s,
          end: s + pegLength - 1,
          size: sz,
          pegLength,
          initialSigns
        });
      }
    }
    return result;
  }

  static getSignBlockForHole(h, blocksOrSettings = {}) {
    const blocks = Array.isArray(blocksOrSettings) ? blocksOrSettings : ObstacleGeometry.getSignConfigs(blocksOrSettings);
    return blocks.find(b => h >= b.start && h <= b.end) || null;
  }

  static getDiceConfigs(settings = {}) {
    if (!settings || !settings.optDice || (settings.brand && settings.brand !== 'clash')) return [];
    const count = Math.min(4, Math.max(1, parseInt(settings.diceCount || settings.optDiceCount, 10) || 1));
    const isClashTarget = [241, 361, 481].includes(Number(settings.target));
    const clashTargetVal = Number(settings.target) || 481;
    const defaultHoles = isClashTarget ? (getClashDefaults(clashTargetVal).diceHoles || [50, 185, 285, 410]) : [20, 50, 80, 100];
    let holes = settings.diceHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings[`optDice${i}Hole`] || settings[`dice${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClashTarget ? (clashTargetVal - 6) : 115;
    const dice = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 5 && h <= maxHole) {
        dice.push({ id: i + 1, hole: h });
      }
    }
    return dice;
  }

  static getDiceForHole(holeNum, diceOrSettings = {}) {
    const dice = Array.isArray(diceOrSettings) ? diceOrSettings : ObstacleGeometry.getDiceConfigs(diceOrSettings);
    return dice.find(d => d.hole === holeNum) || null;
  }

  static getWheelConfigs(settings = {}) {
    if (!settings || (!settings.optWheel && !settings.optSpinningWheel) || (settings.brand && settings.brand !== 'clash')) return [];
    const count = Math.min(4, Math.max(1, parseInt(settings.wheelCount || settings.optWheelCount || settings.optSpinningWheelCount, 10) || 1));
    const isClashTarget = [241, 361, 481].includes(Number(settings.target));
    const clashTargetVal = Number(settings.target) || 481;
    const defaultHoles = isClashTarget ? (getClashDefaults(clashTargetVal).wheelHoles || [65, 160, 260, 385]) : [15, 45, 75, 105];
    let holes = settings.wheelHoles;
    if (!Array.isArray(holes) || !holes.length) {
      const flatHoles = [];
      for (let i = 1; i <= 4; i++) {
        const val = settings[`optWheel${i}Hole`] || settings[`wheel${i}Hole`] || settings[`optSpinningWheel${i}Hole`];
        if (typeof val === 'number' || (typeof val === 'string' && val.trim() !== '')) {
          flatHoles.push(Number(val));
        }
      }
      holes = flatHoles.length ? flatHoles : defaultHoles;
    }
    const maxHole = isClashTarget ? (clashTargetVal - 6) : 115;
    const wheels = [];
    for (let i = 0; i < count; i++) {
      const h = parseInt(holes[i], 10);
      if (!isNaN(h) && h >= 5 && h <= maxHole) {
        wheels.push({ id: i + 1, hole: h });
      }
    }
    return wheels;
  }

  static getWheelForHole(holeNum, wheelsOrSettings = {}) {
    const wheels = Array.isArray(wheelsOrSettings) ? wheelsOrSettings : ObstacleGeometry.getWheelConfigs(wheelsOrSettings);
    return wheels.find(w => w.hole === holeNum) || null;
  }
}
